import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { runMifMarkerUnsupervisedAnalysis } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { getModelsTypeByModelInferences } from 'interfaces/calculateFeatures';
import { BinaryClassifierConfig } from 'interfaces/inferenceArtifacts';
import { JobType, MifMarkerUnsupervisedAnalysisJob } from 'interfaces/job';
import { MapArtifactToSlides } from 'interfaces/jobs/basic';
import { filter, forEach, get, indexOf, isEmpty, map, size } from 'lodash';
import { useSnackbar } from 'notistack';
import { default as React, ReactElement, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { humanize } from 'utils/helpers';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import * as yup from 'yup';
import { ActionStepper } from '../../ActionStepper';
import { JobWithRebuild } from '../../JobWithRebuild';
import { LoadFromPreviousJobStep } from '../../LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from '../../LoadFromPreviousJobStep/usePreviousJobsAvailability';
import InferenceModelsForSlides from '../../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import { useArtifactResultsQuery } from '../../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import { buildSelectedInferenceOrchestrationsFromArtifacts } from '../../PostProcessingAndCalculateFeatures/utils';
import { JobNameAndDescriptionStep } from '../../utils';
import { MultiplexSelectionAlert } from '../MultiplexSelectionAlert';
import { useMultiplexActionSelection } from '../useMultiplexActionSelection';
import { AdditionalParametersStep } from './AdditionalParametersStep';
import {
  applyDefaultMifMarkerUnsupervisedAnalysisJobConfig,
  createDefaultMifMarkerUnsupervisedAnalysisFormValues,
  MifMarkerUnsupervisedAnalysisFormValues,
  runMifMarkerUnsupervisedAnalysisOrder,
  RunMifMarkerUnsupervisedAnalysisStep,
} from './types';

const SNACK_BAR_KEY_RUN_UNSUPERVISED_ANALYSIS = 'RUN_MIF_MARKER_UNSUPERVISED_ANALYSIS';

export interface RunMifMarkerUnsupervisedAnalysisProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  jobId?: string;
}

const requiredPositiveIntegerField = (label: string) =>
  yup
    .number()
    .nullable()
    .typeError(`${label} must be a number`)
    .required(`${label} is required`)
    .integer(`${label} must be an integer`)
    .min(1, `${label} must be greater than 0`);

const requiredNumberField = (label: string) =>
  yup.number().nullable().typeError(`${label} must be a number`).required(`${label} is required`);

const optionalPositiveIntegerField = (label: string) =>
  yup
    .number()
    .nullable()
    .typeError(`${label} must be a number`)
    .integer(`${label} must be an integer`)
    .min(1, `${label} must be greater than 0`);

const validationSchema = {
  [RunMifMarkerUnsupervisedAnalysisStep.LoadParams]: yup.object({}),
  [RunMifMarkerUnsupervisedAnalysisStep.NameAndDescription]: yup.object({}),
  [RunMifMarkerUnsupervisedAnalysisStep.SelectBinaryClassifierResults]: yup.object().shape({
    configParams: yup.object().shape({
      binaryClassifierArtifactUrls: yup
        .array()
        .of(
          yup.object().shape({
            artifactUrlPattern: yup.string().required('Artifact URL Pattern is required'),
            slideIds: yup
              .array()
              .of(yup.string().required('Slide ID is required'))
              .min(1, 'At least one Slide ID is required'),
          })
        )
        .min(1, 'At least one Binary Classifier result is required')
        .required('Binary Classifier results are required'),
    }),
  }),
  [RunMifMarkerUnsupervisedAnalysisStep.AdditionalParameters]: yup.object({
    configParams: yup.object().shape({
      markers: yup
        .array()
        .of(yup.string().required('Channel name is required'))
        .min(1, 'At least one channel must be selected')
        .required('Quantified channels are required'),
      cellSubsamplingParams: yup.object().shape({
        capPerSlide: requiredPositiveIntegerField('Per-slide cap'),
        totalN: requiredPositiveIntegerField('Target cells per slide'),
        threshold: requiredNumberField('Positivity threshold')
          .min(0, 'Positivity threshold must be between 0 and 1')
          .max(1, 'Positivity threshold must be between 0 and 1'),
        minGroupSize: requiredPositiveIntegerField('Minimum group size'),
        topK: requiredPositiveIntegerField('Top groups to sample'),
      }),
      louvainParams: yup.object().shape({
        nDims: optionalPositiveIntegerField('Louvain dimensions override'),
        resolution: requiredNumberField('Louvain resolution').moreThan(0, 'Louvain resolution must be greater than 0'),
      }),
    }),
  }),
};

export const RunMifMarkerUnsupervisedAnalysis = (props: RunMifMarkerUnsupervisedAnalysisProps): ReactElement => {
  const { onClose, casesParams, jobId } = props;
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const studyId = multiplexCasesParams.filters?.studyId;
  const orchestrationSelectionsState = useOrchestrationSelections();
  const { selectedOrchestrations, setSelectedOrchestrations } = orchestrationSelectionsState;
  const [selectedJobId, setSelectedJobId] = useState<string>(undefined);
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);
  const [pendingPreviousJobInferenceSelections, setPendingPreviousJobInferenceSelections] = useState<
    MapArtifactToSlides[] | null
  >(null);

  const [activeStep, setActiveStep] = useState(0);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const [stepWarnings, setStepWarnings] = useState<Record<number, string>>({});

  const currentValidationSchema = validationSchema[runMifMarkerUnsupervisedAnalysisOrder[activeStep]];

  const methods = useForm<MifMarkerUnsupervisedAnalysisFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues: createDefaultMifMarkerUnsupervisedAnalysisFormValues(),
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = methods;

  const runMutation = useMutationWithErrorSnackbar({
    mutationFn: runMifMarkerUnsupervisedAnalysis,
    mutationDescription: 'run mIF Marker Unsupervised Analysis',
    onSuccess: () => {
      enqueueSnackbar('mIF Marker Unsupervised Analysis Started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_UNSUPERVISED_ANALYSIS);
    },
  });

  const checkValidationAndSetIsStepFailed = async (step: RunMifMarkerUnsupervisedAnalysisStep) => {
    const stepIndex = indexOf(runMifMarkerUnsupervisedAnalysisOrder, step);
    const isValid = await trigger();

    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));

    return isValid;
  };

  const setWarnings = (step: RunMifMarkerUnsupervisedAnalysisStep) => {
    const stepIndex = indexOf(runMifMarkerUnsupervisedAnalysisOrder, step);

    if (step === RunMifMarkerUnsupervisedAnalysisStep.SelectBinaryClassifierResults) {
      setStepWarnings((prev) => ({
        ...prev,
        [stepIndex]:
          size(watch('configParams.manifest')) > 0 && size(watch('configParams.manifest')) < size(slides)
            ? "Some slides don't have Binary Classifier results selected, so the job will not run on them"
            : '',
      }));
    }
  };

  const {
    data: binaryClassifierArtifacts,
    isLoading: isBinaryClassifierLoading,
    isFetching: isBinaryClassifierFetching,
    isError: isBinaryClassifierError,
  } = useArtifactResultsQuery({
    caseParams: multiplexCasesParams,
    config: BinaryClassifierConfig,
  });

  const { data: slides, isFetching: isSlidesFetching } = useQuery({
    queryKey: ['slidesDataForArtifactsResults', multiplexCasesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(multiplexCasesParams, signal),
    enabled: Boolean(studyId) && Boolean(effectiveCasesParams),
  });

  useEffect(() => {
    if (pendingPreviousJobInferenceSelections === null || isBinaryClassifierFetching) {
      return;
    }

    setSelectedOrchestrations(
      buildSelectedInferenceOrchestrationsFromArtifacts(
        pendingPreviousJobInferenceSelections,
        binaryClassifierArtifacts || []
      )
    );
    setPendingPreviousJobInferenceSelections(null);
  }, [
    binaryClassifierArtifacts,
    isBinaryClassifierFetching,
    pendingPreviousJobInferenceSelections,
    setSelectedOrchestrations,
  ]);

  useEffect(() => {
    if (pendingPreviousJobInferenceSelections !== null) {
      return;
    }

    const inferenceArtifactUrlPatternsWithSlides: MapArtifactToSlides[] = [];

    const slidesWithResults = filter(map(slides, 'slideId'), (slideId) => {
      return !isEmpty(selectedOrchestrations[slideId]);
    });

    forEach(slidesWithResults, (slideId) => {
      forEach(selectedOrchestrations[slideId], (orchestrationData) => {
        const existingEntry = inferenceArtifactUrlPatternsWithSlides.find(
          (entry) => entry.artifactUrlPattern === orchestrationData?.orchestration.orchestrationResultArtifactUrlPattern
        );
        if (existingEntry) {
          existingEntry.slideIds.push(slideId);
        } else {
          inferenceArtifactUrlPatternsWithSlides.push({
            artifactUrlPattern: orchestrationData?.orchestration.orchestrationResultArtifactUrlPattern,
            slideIds: [slideId],
          });
        }
      });
    });

    setValue('configParams.binaryClassifierArtifactUrls', inferenceArtifactUrlPatternsWithSlides);
    setValue(
      'configParams.manifest',
      inferenceArtifactUrlPatternsWithSlides.flatMap((entry) => entry.slideIds)
    );
    trigger('configParams.binaryClassifierArtifactUrls');
    setWarnings(RunMifMarkerUnsupervisedAnalysisStep.SelectBinaryClassifierResults);
  }, [pendingPreviousJobInferenceSelections, selectedOrchestrations, slides, setValue, trigger]);

  const onSelectedJobParamChange = (newValue: MifMarkerUnsupervisedAnalysisJob) => {
    if (isEmpty(newValue)) {
      setPendingPreviousJobInferenceSelections(null);
      setSelectedOrchestrations({});
      setSelectedJobId(undefined);
      reset(createDefaultMifMarkerUnsupervisedAnalysisFormValues());
    } else {
      const configParams = applyDefaultMifMarkerUnsupervisedAnalysisJobConfig(newValue?.params);

      setSelectedJobId(newValue.id);
      setPendingPreviousJobInferenceSelections(configParams.binaryClassifierArtifactUrls || []);
      reset({
        jobName: newValue?.name ?? '',
        jobDescription: newValue?.description ?? '',
        configParams,
      });
    }
  };

  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(
    JobType.MifMarkerUnsupervisedAnalysis
  );

  const stepsMap = {
    [RunMifMarkerUnsupervisedAnalysisStep.LoadParams]: {
      label: 'Reuse previous job settings',
      optional: true,
      hidden: !isCheckingPreviousJobs && !hasPreviousJobs,
      isLoading: isLoadingSelectedPreviousJob,
      content: (
        <LoadFromPreviousJobStep
          jobType={JobType.MifMarkerUnsupervisedAnalysis}
          onSelectedJob={onSelectedJobParamChange}
          onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
          selectedJobId={selectedJobId}
        />
      ),
    },
    [RunMifMarkerUnsupervisedAnalysisStep.NameAndDescription]: {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
    [RunMifMarkerUnsupervisedAnalysisStep.SelectBinaryClassifierResults]: {
      label: 'Select Binary Classifier Results',
      subLabel: activeStep !== 2 && `${size(watch('configParams.manifest'))}/${size(slides)} results selected`,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {isBinaryClassifierError && <Typography color="error">Error fetching Binary Classifier results</Typography>}
            {get(errors, 'configParams.binaryClassifierArtifactUrls') && (
              <Typography color="error">
                {humanize(get(errors, 'configParams.binaryClassifierArtifactUrls')?.message)}
              </Typography>
            )}
            <InferenceModelsForSlides
              studyId={studyId}
              slides={slides}
              modelsType={getModelsTypeByModelInferences(binaryClassifierArtifacts)}
              inferenceModels={binaryClassifierArtifacts}
              isLoading={isBinaryClassifierLoading || isBinaryClassifierFetching || isSlidesFetching}
              {...orchestrationSelectionsState}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => {
        checkValidationAndSetIsStepFailed(RunMifMarkerUnsupervisedAnalysisStep.SelectBinaryClassifierResults);
        setWarnings(RunMifMarkerUnsupervisedAnalysisStep.SelectBinaryClassifierResults);
      },
    },
    [RunMifMarkerUnsupervisedAnalysisStep.AdditionalParameters]: {
      label: 'Additional Parameters',
      content: (
        <AdditionalParametersStep
          methods={methods}
          checkValidationAndSetIsStepFailed={() =>
            checkValidationAndSetIsStepFailed(RunMifMarkerUnsupervisedAnalysisStep.AdditionalParameters)
          }
          casesParams={multiplexCasesParams}
        />
      ),
      onNextOrBackClick: () =>
        checkValidationAndSetIsStepFailed(RunMifMarkerUnsupervisedAnalysisStep.AdditionalParameters),
    },
  };

  const steps = map(runMifMarkerUnsupervisedAnalysisOrder, (stepId) => stepsMap[stepId]);

  const onSubmit: SubmitHandler<any> = (data: MifMarkerUnsupervisedAnalysisFormValues) => {
    runMutation.mutate({
      ...multiplexCasesParams,
      ...data,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for mIF Marker Unsupervised Analysis to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_UNSUPERVISED_ANALYSIS,
      autoHideDuration: null,
    });
  };

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <JobWithRebuild jobId={jobId} onSelectedJobParamChange={onSelectedJobParamChange}>
      <Grid container direction="column" spacing={2}>
        {multiplexSelectionInfoMessage ? (
          <Grid item>
            <MultiplexSelectionAlert
              severity="info"
              title="This action runs on slides"
              message={multiplexSelectionInfoMessage}
            />
          </Grid>
        ) : null}
        <Grid item>
          <ActionStepper
            handleSubmit={handleSubmit(onSubmit)}
            steps={steps}
            setActiveStepForValidation={setActiveStep}
            isStepFailed={isStepFailed}
            isInitializing={isCheckingPreviousJobs}
            initializingText="Checking for previous jobs…"
            validateStep={() => checkValidationAndSetIsStepFailed(runMifMarkerUnsupervisedAnalysisOrder[activeStep])}
            stepWarnings={stepWarnings}
          />
        </Grid>
      </Grid>
    </JobWithRebuild>
  );
};
